
import { supabase } from "@/integrations/supabase/client";
import { error } from "@/hooks/use-toast";
import { validatePassword } from "@/utils/security";
import { devLog, devError, devWarn } from "@/utils/secureLogger";

export const useSignUp = () => {
  const signUp = async (email: string, password: string, username: string, captchaToken?: string) => {
    try {
      devLog("Starting signup process");
      
      // Validate password strength
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        return { 
          data: null, 
          error: { message: passwordValidation.message }, 
          isPasswordWeak: true 
        };
      }
      
      // Sanitize and normalize inputs
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedUsername = username.trim();
      
      if (!sanitizedUsername) {
        return {
          data: null,
          error: { message: "Username cannot be empty" }
        };
      }
      
      // Check if email already exists via secure RPC
      const { data: exists, error: existsError } = await supabase.rpc('check_user_exists_by_email', {
        user_email: sanitizedEmail,
      });

      if (existsError) {
        devWarn('Email existence check failed, proceeding with signup fallback');
      }

      if (exists === true) {
        devLog("User already exists");
        error({
          title: "Account Already Exists",
          description: "This email is already registered. Please sign in instead.",
        });
        return { data: null, error: { message: "User already registered" }, isExistingUser: true };
      }
      
      // Proceed with signup
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          data: {
            username: sanitizedUsername,
          },
          emailRedirectTo: window.location.origin + '/auth?verification=true',
          captchaToken,
        },
      });
      
      if (signUpError) {
        // If signup is disabled
        if (signUpError.status === 422 && signUpError.message.includes("Signups not allowed")) {
          devLog("Signups are disabled on this Supabase instance");
          return { data: null, error: signUpError, isSignupDisabled: true };
        }
        
        // If user already exists
        if (signUpError.message?.includes("User already registered")) {
          devLog("User already exists");
          error({
            title: "Account Already Exists",
            description: "This email is already registered. Please sign in instead.",
          });
          return { data: null, error: signUpError, isExistingUser: true };
        }
        
        throw signUpError;
      }
      
      devLog("User registration initiated");
      
      // Store email for verification (no password stored)
      sessionStorage.setItem('pendingVerificationEmail', sanitizedEmail);
      
      // Return data to indicate that verification is needed
      return { 
        data: { email: sanitizedEmail, needsVerification: true }, 
        error: null,
        pendingCredentials: { email: sanitizedEmail, password: '', username: sanitizedUsername }
      };
    } catch (err) {
      devError("Error in signup process:", err);
      return { data: null, error: err };
    }
  };

  return { signUp };
};
